import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, LockIcon, ShieldCheckIcon, CheckIcon } from 'lucide-react';
interface CheckoutPageProps {
  onBack: () => void;
}
export function CheckoutPage({
  onBack
}: CheckoutPageProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    confirmEmail: '',
    cardNumber: '',
    expiry: '',
    cvv: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      setIsComplete(true);
    }, 2000);
  };
  const includes = ['Guía práctica paso a paso', 'Estrategias aplicadas de IA', 'Estructura para organizar tu oferta', 'Recursos de apoyo', 'Bonos de implementación', 'Acceso inmediato'];
  if (isComplete) {
    return <div className="min-h-screen bg-cream flex items-center justify-center p-4" data-id="element-324">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="bg-white rounded-3xl p-8 md:p-12 shadow-xl max-w-lg w-full text-center" data-id="element-325">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-326">
            <CheckIcon className="w-10 h-10 text-emerald-600" data-id="element-327" />
          </div>
          <h2 className="text-3xl font-bold text-navy mb-4" data-id="element-328">
            ¡Compra exitosa!
          </h2>
          <p className="text-slate-600 mb-2 text-lg" data-id="element-329">
            Gracias por tu compra de{' '}
            <span className="font-semibold text-navy" data-id="element-330">IA Express 2026</span>.
          </p>
          <p className="text-slate-500 mb-8" data-id="element-331">
            Revisa tu correo electrónico para acceder a tu guía y todos los
            materiales incluidos.
          </p>
          <button onClick={onBack} className="text-gold font-medium hover:underline" data-id="element-332">
            Volver a la página principal
          </button>
        </motion.div>
      </div>;
  }
  return <div className="min-h-screen bg-cream" data-id="element-333">
      {/* Header */}
      <div className="bg-navy py-4 px-4" data-id="element-334">
        <div className="container mx-auto max-w-5xl flex items-center justify-between" data-id="element-335">
          <button onClick={onBack} className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm" data-id="element-336">
            <ArrowLeftIcon className="w-4 h-4" data-id="element-337" />
            Volver
          </button>
          <div className="flex items-center gap-2 text-slate-300 text-sm" data-id="element-338">
            <LockIcon className="w-4 h-4" data-id="element-339" />
            Pago seguro
          </div>
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4 py-8 md:py-12" data-id="element-340">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} data-id="element-341">
          <h1 className="text-3xl md:text-4xl font-bold text-navy mb-2 text-center" data-id="element-342">
            Completa tu compra
          </h1>
          <p className="text-slate-500 text-center mb-10" data-id="element-343">
            Estás a un paso de comenzar tu mini-negocio visual
          </p>

          <div className="grid lg:grid-cols-5 gap-8 items-start" data-id="element-344">
            {/* Order Summary - Right on desktop, top on mobile */}
            <div className="lg:col-span-2 lg:order-2" data-id="element-345">
              <div className="bg-navy rounded-2xl p-6 text-white sticky top-8" data-id="element-346">
                <h3 className="text-lg font-bold text-gold mb-1" data-id="element-347">
                  IA Express 2026
                </h3>
                <p className="text-sm text-slate-300 mb-6" data-id="element-348">
                  Lanza tu mini-negocio visual desde tu celular en 48 horas
                </p>

                <div className="space-y-3 mb-6 border-t border-white/10 pt-6" data-id="element-349">
                  {includes.map((item, i) => <div key={i} className="flex items-center gap-2 text-sm text-slate-300" data-id="element-350">
                      <CheckIcon className="w-4 h-4 text-gold shrink-0" data-id="element-351" />
                      {item}
                    </div>)}
                </div>

                <div className="border-t border-white/10 pt-4" data-id="element-352">
                  <div className="flex justify-between items-center mb-1" data-id="element-353">
                    <span className="text-slate-400 text-sm" data-id="element-354">
                      Precio original
                    </span>
                    <span className="text-slate-400 line-through text-sm" data-id="element-355">
                      $27.00 USD
                    </span>
                  </div>
                  <div className="flex justify-between items-center mb-1" data-id="element-356">
                    <span className="text-sm text-emerald-400 font-medium" data-id="element-357">
                      Descuento 75%
                    </span>
                    <span className="text-sm text-emerald-400" data-id="element-358">-$20.61</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t border-white/10 mt-3" data-id="element-359">
                    <span className="font-bold text-lg" data-id="element-360">Total</span>
                    <span className="font-bold text-2xl text-gold" data-id="element-361">
                      $6.39 USD
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-6 pt-4 border-t border-white/10" data-id="element-362">
                  <ShieldCheckIcon className="w-5 h-5 text-emerald-400 shrink-0" data-id="element-363" />
                  <p className="text-xs text-slate-400" data-id="element-364">
                    Pago único y seguro. Acceso inmediato después de la compra.
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Form */}
            <div className="lg:col-span-3 lg:order-1" data-id="element-365">
              <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-slate-100" data-id="element-366">
                {/* Personal Info */}
                <h3 className="font-bold text-navy text-lg mb-4" data-id="element-367">
                  Información personal
                </h3>
                <div className="space-y-4 mb-8" data-id="element-368">
                  <div data-id="element-369">
                    <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-370">
                      Nombre completo
                    </label>
                    <input id="name" type="text" required value={formData.name} onChange={e => handleChange('name', e.target.value)} placeholder="Tu nombre" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-371" />
                  </div>
                  <div data-id="element-372">
                    <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-373">
                      Correo electrónico
                    </label>
                    <input id="email" type="email" required value={formData.email} onChange={e => handleChange('email', e.target.value)} placeholder="tu@email.com" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-374" />
                  </div>
                  <div data-id="element-375">
                    <label htmlFor="confirmEmail" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-376">
                      Confirmar correo electrónico
                    </label>
                    <input id="confirmEmail" type="email" required value={formData.confirmEmail} onChange={e => handleChange('confirmEmail', e.target.value)} placeholder="Confirma tu correo" className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-377" />
                  </div>
                </div>

                {/* Payment Info */}
                <h3 className="font-bold text-navy text-lg mb-4" data-id="element-378">
                  Información de pago
                </h3>
                <div className="space-y-4 mb-8" data-id="element-379">
                  <div data-id="element-380">
                    <label htmlFor="cardNumber" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-381">
                      Número de tarjeta
                    </label>
                    <input id="cardNumber" type="text" required value={formData.cardNumber} onChange={e => handleChange('cardNumber', e.target.value)} placeholder="1234 5678 9012 3456" maxLength={19} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-382" />
                  </div>
                  <div className="grid grid-cols-2 gap-4" data-id="element-383">
                    <div data-id="element-384">
                      <label htmlFor="expiry" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-385">
                        Fecha de expiración
                      </label>
                      <input id="expiry" type="text" required value={formData.expiry} onChange={e => handleChange('expiry', e.target.value)} placeholder="MM/AA" maxLength={5} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-386" />
                    </div>
                    <div data-id="element-387">
                      <label htmlFor="cvv" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-388">
                        CVV
                      </label>
                      <input id="cvv" type="text" required value={formData.cvv} onChange={e => handleChange('cvv', e.target.value)} placeholder="123" maxLength={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-gold focus:ring-2 focus:ring-gold/20 outline-none transition-all text-slate-800 placeholder:text-slate-400" data-id="element-389" />
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <motion.button type="submit" disabled={isProcessing} whileHover={{
                scale: isProcessing ? 1 : 1.01
              }} whileTap={{
                scale: isProcessing ? 1 : 0.98
              }} className="w-full relative py-4 rounded-full font-bold text-white text-lg overflow-hidden gradient-gold shadow-[0_8px_20px_rgba(212,165,116,0.3)] hover:shadow-[0_12px_25px_rgba(212,165,116,0.5)] transition-shadow disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-390">
                  {isProcessing ? <span className="flex items-center justify-center gap-3" data-id="element-391">
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-392">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-393"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-394"></path>
                      </svg>
                      Procesando...
                    </span> : 'Pagar $6.39 USD'}
                </motion.button>

                <p className="text-xs text-slate-400 text-center mt-4 flex items-center justify-center gap-1.5" data-id="element-395">
                  <LockIcon className="w-3.5 h-3.5" data-id="element-396" />
                  Tu información está protegida con encriptación SSL
                </p>
              </form>
            </div>
          </div>
        </motion.div>
      </div>
    </div>;
}